import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger'
import {
  IsNotEmpty,
  IsOptional,
  IsString,
  IsUUID,
  IsDateString,
  IsEnum,
  IsObject,
  IsArray,
} from 'class-validator'
import {
  PedagogicContentState,
  ContentVisibility,
} from '../../../enums/content.enum'
import { ResourceCapabilities } from '@sphere/shared/domain'
import { ResourceType } from '../../../types/resource.types'
import { ResourceLinkDto } from '../../resources-link/response/resource-link.dto'

export class ResourceDto {
  @ApiProperty({
    description: 'ID de la ressource',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsNotEmpty()
  @IsUUID('4')
  id!: string

  @ApiPropertyOptional({
    description: 'Titre de la ressource',
    example: 'Mathématiques - Chapitre 1',
    type: 'string',
  })
  @IsOptional()
  @IsString()
  title?: string

  @ApiPropertyOptional({
    description: 'Description de la ressource',
    example: 'Ce document contient les bases des mathématiques pour débutants',
    type: 'string',
  })
  @IsOptional()
  @IsString()
  description?: string

  @ApiProperty({
    description: 'URL de la ressource',
    example: 'documents/math-chapitre1.pdf',
    type: 'string',
  })
  @IsNotEmpty()
  @IsString()
  url!: string

  @ApiProperty({
    description: 'Type de ressource',
    enum: ResourceType,
    example: ResourceType.FILE,
  })
  @IsNotEmpty()
  @IsEnum(ResourceType)
  type!: ResourceType

  @ApiPropertyOptional({
    description: 'Métadonnées de la ressource',
  })
  @IsOptional()
  @IsObject()
  metadata?: Record<string, any>

  @ApiPropertyOptional({
    description: 'Paramètres de la ressource',
  })
  @IsOptional()
  @IsObject()
  settings?: Record<string, any>

  @ApiPropertyOptional({
    description: 'État de la ressource',
    enum: PedagogicContentState,
  })
  @IsOptional()
  @IsEnum(PedagogicContentState)
  state?: PedagogicContentState

  @ApiPropertyOptional({
    description: 'Visibilité de la ressource',
    enum: ContentVisibility,
  })
  @IsOptional()
  @IsEnum(ContentVisibility)
  visibility?: ContentVisibility

  @ApiPropertyOptional({
    description: "ID de l'utilisateur propriétaire",
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  ownerUserId?: string

  @ApiPropertyOptional({
    description: "ID de l'utilisateur créateur",
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  createdByUserId?: string

  @ApiPropertyOptional({
    description: 'ID du clone source',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  clonedFromId?: string

  @ApiProperty({
    description: 'Date de création',
  })
  @IsNotEmpty()
  @IsDateString()
  createdAt!: Date

  @ApiProperty({
    description: 'Date de mise à jour',
  })
  @IsNotEmpty()
  @IsDateString()
  updatedAt!: Date

  @ApiPropertyOptional({
    description: 'Date de suppression',
  })
  @IsOptional()
  @IsDateString()
  deletedAt?: Date

  @ApiPropertyOptional({
    description: "ID de l'utilisateur qui a supprimé",
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  deletedByUserId?: string

  @ApiPropertyOptional({
    description: 'Liaison de la ressource',
    type: () => [ResourceLinkDto],
  })
  @IsOptional()
  @IsArray()
  links?: ResourceLinkDto[]

  @ApiPropertyOptional({
    description: 'Capacités de la ressource',
    type: Object,
    additionalProperties: true,
  })
  @IsOptional()
  @IsObject()
  capabilities?: ResourceCapabilities
}
